import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Location } from "@angular/common";
import { Apollo } from 'apollo-angular';
import { PAYMENT_BILL_STATUSES } from '../../../../common/interfaces/global-interfaces';
import { NotificationService } from '../../../../services/notification.service';
import { SettingsService } from '../../../../services/settings.service';
import { AppState } from '../../../../store';
import { StoreItemActions } from '../../../../store/entities/store-item/store-item.actions';
import { StoreItemEntity } from '../../../../store/entities/store-item/store-item.model';
import { CancelBillModalComponent } from '../../../billing-management/billing-list/cancel-bill-modal/cancel-bill-modal.component';
import { ExtendBillModalComponent, ExtendBillResult } from '../../../billing-management/billing-list/extend-bill-modal/extend-bill-modal.component';
import { BillingPrintComponent } from '../../../billing-management/billing-print/billing-print.component';
import { GET_ALL_BILLINGS, REQUEST_CONTROL_NUMBER_MUTATION, CANCEL_BILLING_MUTATION, EXTEND_BILLING_MUTATION, PAY_NOW_BILLING_MUTATION } from '../../../billing-management/billing.graphql';

@Component({
  selector: 'app-corporate-billing-view',
  templateUrl: './corporate-billing-view.component.html',
  styleUrl: './corporate-billing-view.component.scss'
})
export class CorporateBillingViewComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private readonly settings = inject(SettingsService);

  private apollo = inject(Apollo);
  private notify = inject(NotificationService);
  private readonly store = inject(Store<AppState>);
  private dialog = inject(MatDialog);
  location = inject(Location);
  //variables for paymment loading in payNiw
  isPaying = false;
  loaderMessage = "";

  bill: any = null;
  loading = true;
  today = new Date(); // For print timestamp

  ngOnInit(): void {
    const uuid = this.route.snapshot.paramMap.get("uuid")!;
    if (!uuid) {
      this.notify.errorMessage("Invalid URL");
      this.loading = false;
      return;
    }
    this.fetchBill(uuid);
  }

  //FETCH BILL FUNCTIONALITY
  fetchBill(uuid: string) {
    this.loading = true;
    this.apollo
      .watchQuery<any>({
        query: GET_ALL_BILLINGS,
        variables: { filtering: { uuid } },
        fetchPolicy: "network-only",
      })
      .valueChanges.subscribe({
        next: (res) => {
          const items = res.data?.getAllBills?.data || [];
          this.bill = items.find((b: any) => b.uuid === uuid) || null;

          if (!this.bill) {
            this.notify.errorMessage("Bill not found");
          }
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.notify.errorMessage("Failed to load bill");
          this.loading = false;
        },
      });
  }

  // REQUESTING CONTROL NUMBER LOGIC
  requestControlNumber(): void {
    const uuidFromRoute = this.route.snapshot.params["uuid"];
    this.notify
      .confirmation({
        title: "Request Control Number",
        message: `Are you sure you want to request control Number for this bill?`,
      })
      .then((confirmed) => {
        if (confirmed) {
          this.store.dispatch(
            StoreItemActions.createUpdateStoreItem({
              entity: StoreItemEntity.bill,
              mutation: REQUEST_CONTROL_NUMBER_MUTATION,
              input: { billUuid: uuidFromRoute },
              isInput: false,
            })
          );
        }
      });
  }

  //CHECKING IF BILL IS EXPIRED LOGIC
  isBillExpired(): boolean {
    if (!this.bill) return true;

    if (!this.bill.controlNumberExpiredDate) {
      return this.bill.billStatus === "EXPIRED";
    }

    const expiryDate = new Date(this.bill.controlNumberExpiredDate);
    const now = new Date();
    const isExpiredByDate = expiryDate < now;
    const isExpiredByStatus = this.bill.billStatus === "EXPIRED";

    return isExpiredByDate || isExpiredByStatus;
  }

  //CANCEL BILL FUNCTIONALITY
  openCancelModal() {
    if (!this.bill?.controlNumber) {
      this.notify.errorMessage("Cannot cancel: No Control Number issued");
      return;
    }
    if (this.bill?.billStatus === "CANCELLED") {
      this.notify.errorMessage("This bill is already cancelled");
      return;
    }

    const dialogRef = this.dialog.open(CancelBillModalComponent, {
      width: "500px",
      maxWidth: "90vw",
      disableClose: true,
      autoFocus: true,
      data: {
        billId: this.bill.billId,
        controlNumber: this.bill.controlNumber,
      },
    });

    dialogRef.afterClosed().subscribe((reason: string) => {
      if (!reason) return;

      this.apollo
        .mutate<{
          cancelBill: { response: { status: boolean; message: string } };
        }>({
          mutation: CANCEL_BILLING_MUTATION,
          variables: {
            input: {
              billUid: this.bill.uuid,
              reason: reason.trim(),
            },
          },
        })
        .subscribe({
          next: (res) => {
            const response = res.data?.cancelBill?.response;

            if (response?.status) {
              this.notify.successMessage("Bill cancelled successfully");
              this.fetchBill(this.bill.uuid);
            } else {
              this.notify.errorMessage(
                response?.message || "Cancellation failed"
              );
            }
          },
          error: (err) => {
            console.error(err);
            this.notify.errorMessage("Failed to cancel bill");
          },
        });
    });
  }

  //EXTEND BILL FUNCTIONALITY
  extendBill() {
    if (!this.bill?.controlNumber) {
      this.notify.errorMessage("No control number to extend");
      return;
    }

    if (this.isBillExpired()) {
      this.notify.errorMessage("Cannot extend an expired bill");
      return;
    }

    const dialogRef = this.dialog.open(ExtendBillModalComponent, {
      width: "500px",
      maxWidth: "90vw",
      disableClose: true,
      data: {
        billId: this.bill.billId,
        controlNumber: this.bill.controlNumber,
        currentExpiryDate: this.bill.controlNumberExpiredDate,
      },
    });

    dialogRef.afterClosed().subscribe((result: ExtendBillResult) => {
      if (!result) return;

      this.apollo
        .mutate<{
          extendBill: {
            response: { code: number; status: boolean; message: string };
          };
        }>({
          mutation: EXTEND_BILLING_MUTATION,
          variables: {
            input: {
              billUid: this.bill.uuid,
              changeReason: result.changeReason,
              expiryDate: result.expiryDate,
            },
          },
        })
        .subscribe({
          next: (res) => {
            const response = res.data?.extendBill?.response;
            if (response?.status) {
              this.notify.successMessage("Bill extended successfully!");
              this.fetchBill(this.bill.uuid);
            } else {
              this.notify.errorMessage(
                response?.message || "Failed to extend bill"
              );
            }
          },
          error: (err) => {
            console.error("Extend error:", err);
            this.notify.errorMessage("Failed to extend bill");
          },
        });
    });
  }

  //PAY NOW LOGIC
  payNow(): void {
    const billUuid = this.route.snapshot.params["uuid"];
    const isCardPayment = this.bill?.paymentType === "CARD_PAYMENT";

    if (isCardPayment) {
      this.loaderMessage = "Redirecting you to the secure payment gateway...";
    } else {
      this.loaderMessage =
        "Please wait while we generate your control number... This usually takes 5–30 seconds.";
    }

    const variables: any = { billUuid };
    if (isCardPayment) {
      variables.paymentType = "CARD_PAYMENT";
    }

    this.notify
      .confirmation({
        title: "Pay Bill",
        message: "Are you sure you want to proceed with payment?",
      })
      .then((confirmed) => {
        if (!confirmed) return;

        // Show the loader immediately
        this.isPaying = true;

        this.apollo
          .mutate<{
            payNow: {
              response: { code: number; status: boolean; message: string };
              data: { cardPaymentUrl?: string };
            };
          }>({
            mutation: PAY_NOW_BILLING_MUTATION,
            variables,
          })
          .subscribe({
            next: (result) => {
              const cardPaymentUrl = result.data?.payNow.data?.cardPaymentUrl;

              if (cardPaymentUrl) {
                setTimeout(() => {
                  window.location.href = cardPaymentUrl;
                }, 2000);
              } else {
                this.isPaying = false;
                this.notify.successMessage(
                  "Control number generated successfully!"
                );
                this.fetchBill(billUuid);
              }
            },
            error: (error) => {
              this.isPaying = false; // Always hide loader on error
              console.error("PayNow error:", error);
              this.notify.errorMessage(
                error?.message || "Payment initiation failed. Please try again."
              );
            },
          });
      })
      .catch(() => {
        // User cancelled the confirmation dialog
        this.isPaying = false;
      });
  }

  findStatusClass(statusValue) {
    return PAYMENT_BILL_STATUSES.find((status) => status.key == statusValue)
      ?.classes;
  }

  // PRINT FUNCTIONALITY
  // In billing-view.component.ts - REPLACE YOUR printBill() METHOD

  printBill() {
    if (!this.bill) {
      this.notify.errorMessage("No bill data to print");
      return;
    }

    // Open dialog and pass UUID
    this.dialog.open(BillingPrintComponent, {
      width: "90vw",
      maxWidth: "1200px",
      height: "90vh",
      disableClose: false,
      data: {
        billUuid: this.bill.uuid, // Pass UUID here
      },
    });
  }

  // Helper to convert number to words
  convertAmountToWords(amount: number): string {
    if (!amount) return "ZERO (TZS)";

    const ones = [
      "",
      "ONE",
      "TWO",
      "THREE",
      "FOUR",
      "FIVE",
      "SIX",
      "SEVEN",
      "EIGHT",
      "NINE",
    ];
    const tens = [
      "",
      "",
      "TWENTY",
      "THIRTY",
      "FORTY",
      "FIFTY",
      "SIXTY",
      "SEVENTY",
      "EIGHTY",
      "NINETY",
    ];
    const teens = [
      "TEN",
      "ELEVEN",
      "TWELVE",
      "THIRTEEN",
      "FOURTEEN",
      "FIFTEEN",
      "SIXTEEN",
      "SEVENTEEN",
      "EIGHTEEN",
      "NINETEEN",
    ];

    const convertHundreds = (num: number): string => {
      let str = "";
      if (num >= 100) {
        str += ones[Math.floor(num / 100)] + " HUNDRED ";
        num %= 100;
      }
      if (num >= 20) {
        str += tens[Math.floor(num / 10)] + " ";
        num %= 10;
      } else if (num >= 10) {
        str += teens[num - 10] + " ";
        return str;
      }
      if (num > 0) {
        str += ones[num] + " ";
      }
      return str;
    };

    let words = "";

    if (amount >= 1000000) {
      words += convertHundreds(Math.floor(amount / 1000000)) + "MILLION ";
      amount %= 1000000;
    }

    if (amount >= 1000) {
      words += convertHundreds(Math.floor(amount / 1000)) + "THOUSAND ";
      amount %= 1000;
    }

    words += convertHundreds(amount);

    return words.trim() + " (TZS)";
  }

  // Helper to get bill description
  getBillDescription(): string {
    if (!this.bill?.billItems || this.bill.billItems.length === 0) {
      return "PAYMENT";
    }

    if (this.bill.billItems.length === 1) {
      return this.bill.billItems[0].productName.toUpperCase();
    }

    return this.bill.billItems
      .map((item: any) => item.productName)
      .join(", ")
      .toUpperCase();
  }
}

